import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Button, Checkbox, createStyles, FormControlLabel, Grid, Icon, makeStyles, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Action, Column, Options } from 'material-table';

import { ClientModel, clientModelInitial } from '../../../Models/ClientModel';
import { ClipperModel } from '../../../Models/ClipperModel';
import ClientService from '../../../Services/ClientService';
import ClipperService from '../../../Services/ClipperService';
import { BasicPage } from '../../../shared/components/BasicPage';
import { TableCommon } from '../../../shared/components/TableCommon';
import { TableEmpty } from '../../../shared/components/TableEmpty';
import { TableLoading } from '../../../shared/components/TableLoading';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { SearchStatus } from '../../../shared/enum/SearchStatus';
import { getTableColumn } from '../../../shared/operators';
import { HomeContext } from '../../Home/HomeContext';
import { ClipperContext } from '../ClipperContext';
import { SearchButtons } from '../../../shared/components/SearchButtons';

const useStyles = makeStyles(() => createStyles({
    inactiveCheckbox: {
        alignSelf: 'center'
    },
    button: {
        paddingLeft: 0,
        '&:hover': {
            textDecorationLine: 'underline',
        },
    },
}));

export function ClipperSearch() {
    const classes = useStyles();

    const homeContext = React.useContext(HomeContext);
    const clipperContext = React.useContext(ClipperContext);

    const [clientList, setClientList] = React.useState<ClientModel[]>([]);
    const [clipperNameFilter, setClipperNameFilter] = React.useState<string>('');
    const [selectedClientFilter, setSelectedClientFilter] = React.useState<ClientModel>(clientModelInitial);
    const [includeInactiveFilter, setIncludeInactiveFilter] = React.useState<boolean>(false);
    const [searchStatus, setSearchStatus] = React.useState<SearchStatus>(SearchStatus.INITIAL);
    const [tableClippers, setTableClippers] = React.useState<ClipperModel[]>([]);

    React.useEffect(() => {
        if (homeContext.pageLocation === PageLocation.CLIPPER_SEARCH) {
            homeContext.setOpenLoadingDialog(true);

            ClientService.clientGetAll(true)
                .then((res: AxiosResponse) => setClientList(res.data))
                .catch((error: AxiosError) => homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar os clientes. <br/>', error))
                .finally(() => { searchStatus != SearchStatus.INITIAL && homeContext.setOpenLoadingDialog(false); });

            if (searchStatus === SearchStatus.INITIAL)
                fetchInitialClippers();
        }
    }, [homeContext.pageLocation]);

    const tableColumns: Column<ClipperModel>[] = [
        getTableColumn('Nome da clipadora', 'name', 'string', 'left', undefined, 'asc'),
        {
            title: 'Status',
            render: (rowData : ClipperModel) => (rowData.active ? 'Ativo' : 'Inativo'),
            field: 'active',
            type: 'string',
            align: 'left'
        },
    ];

    const tableActions: (Action<any> | ((rowData: any) => Action<any>))[] = [
        {
            icon: 'edit',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Editar clipadora',
            onClick: (_, rowData: ClipperModel) => { clipperContext.setClipper(rowData); homeContext.setPageLocation(PageLocation.CLIPPER_EDIT); }
        }
    ];

    const tableOptions: Options<ClipperModel> = {
        filtering: true,
        sorting: true,
        toolbar: false,
        showTitle: false,
        actionsColumnIndex: -1,
        pageSizeOptions: [5, 10, 15],
        pageSize: 10
    };
    
    const handleCleanFilter = () => {
        setClipperNameFilter('');
        setSelectedClientFilter(clientModelInitial);
        setIncludeInactiveFilter(false);
    };

    const fetchInitialClippers = () => {
        ClipperService.getClipperByFilters('')
            .then((res: AxiosResponse) => setTableClippers(res.data))
            .catch((error: AxiosError) => homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar as clipadoras. <br/>', error))
            .finally(() => {searchStatus === SearchStatus.INITIAL && homeContext.setOpenLoadingDialog(false);});
    };

    const handleSearch = () => {
        setSearchStatus(SearchStatus.LOADING);

        ClipperService.getClipperByFilters(clipperNameFilter, selectedClientFilter.id, includeInactiveFilter)
            .then((res: AxiosResponse) => {
                if(res.data){
                    setTableClippers(res.data);

                    if(res.data.length > 0)
                        setSearchStatus(SearchStatus.RESULT);
                    else
                        setSearchStatus(SearchStatus.EMPTY);
                }
            })
            .catch((error: AxiosError) => {
                setTableClippers([]);
                setSearchStatus(SearchStatus.EMPTY);
                homeContext.showError('Ops... Ocorreu um erro e não foi possível carregar as clipadoras. <br/>', error);
            });
    };

    const getClipperNameFilter = () => {
        return (
            <Grid item xs={12} sm={5}>
                <TextField 
                    label="Nome da clipadora"
                    variant="outlined"
                    fullWidth
                    type="text"
                    value={clipperNameFilter}
                    onChange={(event) => setClipperNameFilter(event.target.value)} />
            </Grid>
        );
    };

    const getClientFilter = () => {
        return (
            <Grid item xs={12} sm={4}>
                <Autocomplete
                    id="client-filter"
                    value={selectedClientFilter}
                    onChange={(_, value: ClientModel | null) => {
                        if (value)
                            setSelectedClientFilter(value);
                        else
                            setSelectedClientFilter(clientModelInitial);
                    }}
                    options={clientList}
                    getOptionLabel={(option: ClientModel) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Cliente"
                            variant="outlined"/>
                    }/>
            </Grid>
        );
    };

    const getShowInactive = () => {
        return (
            <Grid item xs={12} sm={3} className={classes.inactiveCheckbox}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeInactiveFilter}
                            onChange={() => setIncludeInactiveFilter(!includeInactiveFilter)}
                            name="Exibir clipadoras inativas"
                            color="primary" />
                    }
                    label="Exibir clipadoras inativas" />
            </Grid>
        );
    };

    const getResultTable = () => {
        if (searchStatus === SearchStatus.LOADING) {
            return (
                <Grid item xs={12}>
                    <TableLoading displayText="Carregando clipadoras..." />
                </Grid>
            );
        }
        else if (searchStatus === SearchStatus.EMPTY) {
            return (
                <Grid item xs={12}>
                    <TableEmpty
                        displayText="Nenhuma clipadora encontrada"
                        buttonText="Adicionar nova clipadora"
                        pageLocation={PageLocation.CLIPPER_INPUT}/>
                </Grid>
            );
        }
        else if (searchStatus === SearchStatus.INITIAL || searchStatus === SearchStatus.RESULT) {
            return (
                <Grid item xs={12}>
                    <>
                        <Button
                            onClick={() => homeContext.setPageLocation(PageLocation.CLIPPER_INPUT)}
                            size='large'
                            startIcon={<Icon color='primary'>add</Icon>}
                            className={classes.button}>
                            ADICIONAR NOVA CLIPADORA
                        </Button>
                        <TableCommon
                            tableData={tableClippers}
                            tableColumns={tableColumns}
                            emptyDataSourceMessage='Nenhuma clipadora encontrada.'
                            tableActions={tableActions}
                            extraOptions={tableOptions}/>
                    </>
                </Grid>
            );
        }
    };

    return (
        <BasicPage
            title="Consulta de clipadoras"
            subtitle="Para consultar uma clipadora, basta navegar pela tabela abaixo ou inserir um filtro de busca.">
            <>
                { getClipperNameFilter() }
                { getClientFilter() }
                { getShowInactive() }

                <SearchButtons
                    searchStatus={searchStatus}
                    handleCleanFilter={handleCleanFilter}
                    handleSearch={handleSearch}/>

                { getResultTable() }
            </>
        </BasicPage>
    );
}